import type { WorkoutGpsTrace, WorkoutGpsTraceSample } from '../../lib/api/adminMobileUsers'

export const TRACE_ASCENT_COLOR = '#047857'
export const TRACE_DESCENT_COLOR = '#dc2626'
export const TRACE_FLAT_COLOR = '#22c55e'
export const TRACE_UNKNOWN_ELEVATION_COLOR = '#64748b'

const SOFT_ASCENT_COLOR = '#86efac'
const SOFT_DESCENT_COLOR = '#fca5a5'
const MAX_VISUAL_DELTA_METERS = 12
const FLAT_DELTA_METERS = 0.25

export type WorkoutGpsSampleInfo = {
  sample: WorkoutGpsTraceSample
  index: number
  elevationDeltaMeters: number | null
  acceptedElevationDeltaMeters: number | null
  acceptedCumulativeGainMeters: number
  acceptedCumulativeLossMeters: number
}

export type ElevationTraceSegment = {
  state: 'running' | 'paused'
  positions: [[number, number], [number, number]]
  elevationDeltaMeters: number | null
  color: string
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const hexToRgb = (hex: string) => {
  const normalized = hex.replace('#', '')
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16)
  }
}

const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }) =>
  `#${[r, g, b].map((value) => Math.round(value).toString(16).padStart(2, '0')).join('')}`

const mixHex = (from: string, to: string, ratio: number) => {
  const a = hexToRgb(from)
  const b = hexToRgb(to)
  const t = clamp(ratio, 0, 1)
  return rgbToHex({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t
  })
}

const formatNumber = (value: number | null | undefined, fractionDigits = 3) =>
  isFiniteNumber(value) ? value.toFixed(fractionDigits) : ''

const timestampToIso = (timestamp: number) => {
  if (!Number.isFinite(timestamp) || timestamp <= 0) return ''
  try {
    return new Date(timestamp).toISOString()
  } catch {
    return ''
  }
}

const filenameSafe = (value: string) =>
  value
    .trim()
    .replace(/[^a-z0-9_-]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'workout'

export const getElevationDeltaColor = (deltaMeters: number | null | undefined) => {
  if (!isFiniteNumber(deltaMeters)) return TRACE_UNKNOWN_ELEVATION_COLOR
  if (Math.abs(deltaMeters) <= FLAT_DELTA_METERS) return TRACE_FLAT_COLOR

  const ratio = clamp(Math.abs(deltaMeters) / MAX_VISUAL_DELTA_METERS, 0, 1)
  if (deltaMeters > 0) {
    return mixHex(SOFT_ASCENT_COLOR, TRACE_ASCENT_COLOR, ratio)
  }
  return mixHex(SOFT_DESCENT_COLOR, TRACE_DESCENT_COLOR, ratio)
}

export const buildWorkoutGpsSampleInfos = (samples: WorkoutGpsTraceSample[]): WorkoutGpsSampleInfo[] => {
  let previousAltitudeMeters: number | null = null
  let previousAcceptedAltitudeMeters: number | null = null
  let acceptedCumulativeGainMeters = 0
  let acceptedCumulativeLossMeters = 0

  return samples.map((sample, index) => {
    const altitudeMeters = isFiniteNumber(sample.altitudeMeters) ? sample.altitudeMeters : null
    const elevationDeltaMeters =
      altitudeMeters != null && previousAltitudeMeters != null
        ? altitudeMeters - previousAltitudeMeters
        : null
    let acceptedElevationDeltaMeters: number | null = null

    if (sample.accepted && altitudeMeters != null && previousAcceptedAltitudeMeters != null) {
      acceptedElevationDeltaMeters = altitudeMeters - previousAcceptedAltitudeMeters
      if (acceptedElevationDeltaMeters > 0) {
        acceptedCumulativeGainMeters += acceptedElevationDeltaMeters
      } else if (acceptedElevationDeltaMeters < 0) {
        acceptedCumulativeLossMeters += Math.abs(acceptedElevationDeltaMeters)
      }
    }

    if (altitudeMeters != null) {
      previousAltitudeMeters = altitudeMeters
      if (sample.accepted) {
        previousAcceptedAltitudeMeters = altitudeMeters
      }
    }

    return {
      sample,
      index,
      elevationDeltaMeters,
      acceptedElevationDeltaMeters,
      acceptedCumulativeGainMeters,
      acceptedCumulativeLossMeters
    }
  })
}

export const buildElevationTraceSegments = (samples: WorkoutGpsTraceSample[]): ElevationTraceSegment[] => {
  const acceptedSamples = samples.filter((sample) => sample.accepted)
  const segments: ElevationTraceSegment[] = []

  for (let index = 1; index < acceptedSamples.length; index += 1) {
    const previous = acceptedSamples[index - 1]
    const current = acceptedSamples[index]
    const previousAltitude = isFiniteNumber(previous.altitudeMeters) ? previous.altitudeMeters : null
    const currentAltitude = isFiniteNumber(current.altitudeMeters) ? current.altitudeMeters : null
    const elevationDeltaMeters =
      previousAltitude != null && currentAltitude != null
        ? currentAltitude - previousAltitude
        : null

    segments.push({
      state: current.state === 'paused' ? 'paused' : 'running',
      positions: [
        [previous.latitude, previous.longitude],
        [current.latitude, current.longitude]
      ],
      elevationDeltaMeters,
      color: getElevationDeltaColor(elevationDeltaMeters)
    })
  }

  return segments
}

export const buildWorkoutGpsTraceCsv = (trace: WorkoutGpsTrace) => {
  const infos = buildWorkoutGpsSampleInfos(trace.samples)
  const header = [
    'workoutId',
    'userId',
    'recordedAt',
    'traceTruncated',
    'sampleIndex',
    'timestampMs',
    'timestampIso',
    'latitude',
    'longitude',
    'state',
    'accepted',
    'rejectedReason',
    'source',
    'accuracyMeters',
    'speedMps',
    'altitudeMeters',
    'elevationDeltaMeters',
    'acceptedElevationDeltaMeters',
    'acceptedCumulativeGainMeters',
    'acceptedCumulativeLossMeters'
  ]

  const rows = infos.map((info) => {
    const sample = info.sample
    return [
      trace.workoutId,
      trace.userId,
      trace.recordedAt,
      String(trace.truncated),
      String(info.index + 1),
      Number.isFinite(sample.timestamp) && sample.timestamp > 0 ? String(sample.timestamp) : '',
      timestampToIso(sample.timestamp),
      formatNumber(sample.latitude, 7),
      formatNumber(sample.longitude, 7),
      sample.state,
      String(sample.accepted),
      sample.rejectedReason ?? '',
      sample.source ?? '',
      formatNumber(sample.accuracyMeters, 2),
      formatNumber(sample.speedMps, 3),
      formatNumber(sample.altitudeMeters, 2),
      formatNumber(info.elevationDeltaMeters, 2),
      formatNumber(info.acceptedElevationDeltaMeters, 2),
      formatNumber(info.acceptedCumulativeGainMeters, 2),
      formatNumber(info.acceptedCumulativeLossMeters, 2)
    ]
  })

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  const filename = `hikestrong-gps-samples-${filenameSafe(trace.userId)}-${filenameSafe(trace.workoutId)}-${timestamp}.csv`

  return { header, rows, filename }
}
